import { Injectable } from '@angular/core';
import { CaiApi } from '../../utils/cai-api';
import { API_CORE } from '../../constant/api-core.const';
import { IPaginationResults, IWeightTypeRate } from '../../model';
import { IAirlineRate } from '../../model/airline-rate.model';
import { IBlobFile } from '../../model/blob-file.model';
import { IRateAirlineProdCode } from '../../model/rate-airline-prod-code.model';
import { IRateInsight } from '../../model/rate-insight.model';
import { IRateMetadata } from '../../model/rate-metadata.model';
import { IRateData } from '../../model/rate-data.model';

@Injectable({
  providedIn: 'root',
})
export class RateService {
  async getAllRates(
    page: number,
    size: number,
    sort: string,
    airline?: string,
    selectedOfficeId?: number,
    country?: string,
    filterHistory?: boolean,
    search?: string,
    origin?: string,
    commodity?: string,
    destination?: string,
    rateCategory?: string,
    allIn?: string,
    shcs?: string,
    aircraftTypes?: string,
    type?: string,
    iatacass?: string,
    departureDate?: string,
    markup?: number,
    chargeableWeight?: number,
  ): Promise<IPaginationResults<IAirlineRate>> {
    const extraOptions: any = {
      queryParams: {
        page,
        size,
        sort,
      },
    };
    if (country) {
      extraOptions.queryParams.country = country;
    }
    if (origin) {
      extraOptions.queryParams.origin = origin;
    }
    if (airline) {
      extraOptions.queryParams.airline = airline;
    }
    if (selectedOfficeId) {
      extraOptions.queryParams.officeId = selectedOfficeId;
    }
    if (filterHistory) {
      extraOptions.queryParams.filterHistory = filterHistory;
    }
    if (search) {
      extraOptions.queryParams.search = search;
    }
    if (commodity) {
      extraOptions.queryParams.commodity = commodity;
    }
    if (destination) {
      extraOptions.queryParams.destination = destination;
    }
    if (rateCategory) {
      extraOptions.queryParams.rateCategory = rateCategory;
    }
    if (shcs) {
      extraOptions.queryParams.shcs = shcs;
    }
    if (aircraftTypes) {
      extraOptions.queryParams.aircraftType = aircraftTypes;
    }
    if (type) {
      extraOptions.queryParams.type = type;
    }
    if (iatacass) {
      extraOptions.queryParams.iatacass = iatacass;
    }
    if (departureDate) {
      extraOptions.queryParams.departureDate = departureDate;
    }
    if (markup) {
      extraOptions.queryParams.markup = markup;
    }
    if (allIn) {
      extraOptions.queryParams.allIn = allIn;
    }
    if (chargeableWeight) {
      extraOptions.queryParams.chargeableWeight = chargeableWeight;
    }
    return CaiApi.get(API_CORE.name, API_CORE.URLs.Rate.Rates, extraOptions);
  }

  async exportExcel(
    airline?: string,
    selectedOfficeId?: number,
    origin?: string,
    country?: string,
    search?: string,
    columns?: string,
    commodity?: string,
    destination?: string,
    rateCategory?: string,
    allIn?: string,
    aircraftTypes?: string,
    type?: string,
    iatacass?: string,
    departureDate?: string,
    markup?: number,
    chargeableWeight?: number,
  ): Promise<IBlobFile> {
    const extraOptions: any = {
      queryParams: {},
    };
    if (airline) {
      extraOptions.queryParams.airline = airline;
    }
    if (selectedOfficeId) {
      extraOptions.queryParams.officeId = selectedOfficeId;
    }
    if (country) {
      extraOptions.queryParams.country = country;
    }
    if (origin) {
      extraOptions.queryParams.origin = origin;
    }
    if (rateCategory) {
      extraOptions.queryParams.rateCategory = rateCategory;
    }
    if (aircraftTypes) {
      extraOptions.queryParams.aircraftType = aircraftTypes;
    }
    if (destination) {
      extraOptions.queryParams.destination = destination;
    }
    if (commodity) {
      extraOptions.queryParams.commodity = commodity;
    }
    if (type) {
      extraOptions.queryParams.type = type;
    }
    if (iatacass) {
      extraOptions.queryParams.iatacass = iatacass;
    }
    if (search) {
      extraOptions.queryParams.search = search;
    }
    if (columns) {
      extraOptions.queryParams.columns = columns;
    }
    if (departureDate) {
      extraOptions.queryParams.departureDate = departureDate;
    }
    if (allIn) {
      extraOptions.queryParams.allIn = allIn;
    }
    if (markup) {
      extraOptions.queryParams.markup = markup;
    }
    if (chargeableWeight) {
      extraOptions.queryParams.chargeableWeight = chargeableWeight;
    }
    return CaiApi.get(
      API_CORE.name,
      API_CORE.URLs.Rate.ExportCSV,
      extraOptions,
    );
  }

  async getRatesForMetadata(
    airline?: string,
    selectedOfficeId?: number,
    country?: string,
    rateCategory?: string,
    iatacass?: string,
    types?: string[],
    requirement?: string,
  ): Promise<IRateMetadata> {
    const extraOptions: any = {
      queryParams: {},
    };
    if (airline) {
      extraOptions.queryParams.airline = airline;
    }
    if (selectedOfficeId) {
      extraOptions.queryParams.officeId = selectedOfficeId;
    }
    if (country) {
      extraOptions.queryParams.country = country;
    }
    if (rateCategory) {
      extraOptions.queryParams.rateCategory = rateCategory;
    }
    if (iatacass) {
      extraOptions.queryParams.iatacass = iatacass;
    }
    if (types && types.length > 0) {
      extraOptions.queryParams.type = types.join(',');
    }
    if (requirement) {
      extraOptions.queryParams.requirement = requirement;
    }
    return CaiApi.get(API_CORE.name, API_CORE.URLs.Rate.Metadata, extraOptions);
  }

  async getRatesList(
    airline?: string,
    selectedOfficeId?: number,
    country?: string,
    rateCategory?: string,
    iatacass?: string,
    types?: string[],
  ): Promise<IRateData[]> {
    const extraOptions: any = {
      queryParams: {},
    };
    if (airline) {
      extraOptions.queryParams.airline = airline;
    }
    if (selectedOfficeId) {
      extraOptions.queryParams.officeId = selectedOfficeId;
    }
    if (country) {
      extraOptions.queryParams.country = country;
    }
    if (rateCategory) {
      extraOptions.queryParams.rateCategory = rateCategory;
    }
    if (iatacass) {
      extraOptions.queryParams.iatacass = iatacass;
    }
    if (types && types.length > 0) {
      extraOptions.queryParams.type = types.join(',');
    }
    return CaiApi.get(API_CORE.name, API_CORE.URLs.Rate.List, extraOptions);
  }

  async getRateCoverage(
    origin: string,
    destination: string,
    commodity: string,
    officeId: number,
    departureDate: string,
  ): Promise<string[]> {
    const extraOptions: any = {
      queryParams: {
        origin,
        destination,
        commodity,
        officeId,
        departureDate,
      },
    };
    return CaiApi.get(API_CORE.name, API_CORE.URLs.Rate.Coverage, extraOptions);
  }

  async getRateInsight(
    airline: string,
    origin: string,
    destination: string,
    commodity: string,
    chargeableWeight: number,
    officeId: number,
    departureDate: string,
    originCountry?: string,
    destinationCountry?: string,
  ): Promise<IRateInsight> {
    const extraOptions: any = {
      queryParams: {
        airline,
        origin,
        destination,
        commodity,
        chargeableWeight,
        officeId,
        departureDate,
      },
    };
    if (originCountry) {
      extraOptions.queryParams.originCountry = originCountry;
    }
    if (destinationCountry) {
      extraOptions.queryParams.destinationCountry = destinationCountry;
    }
    return CaiApi.get(API_CORE.name, API_CORE.URLs.Rate.Insights, extraOptions);
  }

  async getAirlineProdCodes(
    officeId: number,
    airline?: string,
    rateTable?: string,
  ): Promise<IRateAirlineProdCode[]> {
    const extraOptions: any = {
      queryParams: {},
    };
    if (officeId) {
      extraOptions.queryParams.officeId = officeId;
    }
    if (airline) {
      extraOptions.queryParams.airline = airline;
    }
    if (rateTable) {
      extraOptions.queryParams.type = rateTable;
    }
    return CaiApi.get(
      API_CORE.name,
      API_CORE.URLs.Rate.ProdCodes,
      extraOptions,
    );
  }

  async getRateWeightTypes(): Promise<IWeightTypeRate[]> {
    return CaiApi.get(API_CORE.name, API_CORE.URLs.Rate.WeightTypes, {});
  }
}
